import { DateFieldOption } from './useDateField.types';

// Cache meridiem options per locale
const meridiemCache: Record<string, Array<DateFieldOption>> = {};

// Predefined timestamps for AM and PM (9 AM / 9 PM)
const AM_TIMESTAMP = new Date(2020, 0, 1, 9).getTime();
const PM_TIMESTAMP = new Date(2020, 0, 1, 21).getTime();

/**
 * Cleans a formatted time string by:
 * - Removing digits and punctuation
 * - Removing invisible LTR/RTL markers
 * - Forcing uppercase for Latin letters only
 */
const cleanMeridiem = (str: string): string => {
    const text = str
        .replace(/[\p{Number}\s:]+/gu, '') // Remove digits and spaces/colons
        .replace(/[\u200E\u200F]/g, '') // Remove LTR/RTL marks
        .trim();

    // Force uppercase for Latin letters only
    return /^[A-Za-z]+$/.test(text) ? text.toUpperCase() : text;
};

/**
 * Returns AM/PM options translated for the given locale.
 * Uses caching per locale to avoid recomputation.
 */
export const getLocalisedMeridiemOptions = (locale: string): Array<DateFieldOption> => {
    if (meridiemCache[locale]) {
        return meridiemCache[locale];
    }

    const format = new Intl.DateTimeFormat(locale, { hour: 'numeric', hour12: true });

    const amDate = new Date(AM_TIMESTAMP);
    const pmDate = new Date(PM_TIMESTAMP);

    const options: Array<DateFieldOption> = [
        { value: 'AM', text: cleanMeridiem(format.format(amDate)) },
        { value: 'PM', text: cleanMeridiem(format.format(pmDate)) },
    ];

    meridiemCache[locale] = options;
    return options;
};
